/**
 * "Choose the part to use": a clip is rarely good from its first frame. The whole source runs along a bar of stills;
 * the part that plays is the bright window — drag it to slide it, drag its edges to make it longer or shorter, or
 * click anywhere on the bar to move it there. The preview loops just that part. Keys: Space play, I / O set the
 * start / end at the current frame, ← → nudge (⇧ = 1 s), Enter use it, Esc cancel.
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Loader2, Pause, Play, Scissors, Volume2, VolumeX, X } from 'lucide-react';

const r1 = (n: number) => Math.round(n * 10) / 10;
const fmt = (t: number) => { const m = Math.floor(t / 60); const s = t - m * 60; return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`; };
const THUMBS = 8;
const MIN_LEN = 0.5;

export function ClipTrimmer({ url, title, initialFrom = 0, initialSeconds, maxSeconds = 30, onCancel, onUse }: {
  url: string;
  title?: string;
  /** Where the part starts in the source, and how long it is (source seconds). */
  initialFrom?: number;
  initialSeconds?: number;
  maxSeconds?: number;
  onCancel: () => void;
  onUse: (from: number, seconds: number) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [dur, setDur] = useState<number | null>(null);
  const [from, setFrom] = useState(initialFrom);
  const [len, setLen] = useState(initialSeconds ?? 3);
  const [cur, setCur] = useState(initialFrom);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const drag = useRef<{ kind: 'body' | 'in' | 'out'; x0: number; from: number; len: number } | null>(null);
  const win = useRef({ from, len });
  win.current = { from, len };

  // The source's length decides the bar; the window is kept inside it.
  const onMeta = () => {
    const v = video.current; if (!v || !Number.isFinite(v.duration)) return;
    const d = v.duration;
    setDur(d);
    const l = Math.max(MIN_LEN, Math.min(initialSeconds ?? Math.min(3, d), d, maxSeconds));
    const f = Math.max(0, Math.min(initialFrom, d - l));
    setLen(r1(l)); setFrom(r1(f)); v.currentTime = f;
    void v.play().catch(() => setPlaying(false));
  };
  // The preview loops the chosen part (and follows the window while it is dragged).
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = video.current;
      if (v) {
        const { from: f, len: l } = win.current;
        if (v.currentTime >= f + l - 0.03 || v.currentTime < f - 0.25) v.currentTime = f;
        setCur(v.currentTime);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const togglePlay = useCallback(() => {
    const v = video.current; if (!v) return;
    if (v.paused) { void v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  }, []);
  const place = useCallback((f: number, l: number, seekTo?: number) => {
    if (dur == null) return;
    const L = Math.max(MIN_LEN, Math.min(l, dur, maxSeconds));
    const F = Math.max(0, Math.min(f, dur - L));
    setFrom(r1(F)); setLen(r1(L));
    const v = video.current; if (v) v.currentTime = seekTo ?? F;
  }, [dur, maxSeconds]);
  const confirm = useCallback(() => { if (dur != null) onUse(r1(from), r1(len)); }, [dur, from, len, onUse]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest?.('input, textarea')) return;
      const v = video.current;
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      else if (e.key === 'Enter') { e.preventDefault(); confirm(); }
      else if (e.key === ' ') { e.preventDefault(); togglePlay(); }
      else if ((e.key === 'i' || e.key === 'I') && v) { const t = v.currentTime; place(t, from + len - t); }
      else if ((e.key === 'o' || e.key === 'O') && v) { const t = v.currentTime; if (t - from >= MIN_LEN) place(from, t - from, from); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); const step = (e.shiftKey ? 1 : 0.1) * (e.key === 'ArrowLeft' ? -1 : 1); place(from + step, len); }
      else return;
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [from, len, onCancel, place, togglePlay, confirm]);

  const secAt = (clientX: number) => { const r = bar.current!.getBoundingClientRect(); return Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * (dur || 0); };
  const down = (e: ReactPointerEvent, kind: 'body' | 'in' | 'out') => {
    e.stopPropagation(); e.preventDefault();
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch { /* synthetic */ }
    drag.current = { kind, x0: e.clientX, from, len };
  };
  const move = (e: ReactPointerEvent) => {
    const d = drag.current; if (!d || dur == null || !bar.current) return;
    const ds = ((e.clientX - d.x0) / bar.current.getBoundingClientRect().width) * dur;
    if (d.kind === 'body') place(d.from + ds, d.len);
    else if (d.kind === 'in') { const f = Math.max(0, Math.min(d.from + d.len - MIN_LEN, d.from + ds)); place(f, d.from + d.len - f); }
    else { const l = Math.max(MIN_LEN, d.len + ds); place(d.from, l, Math.max(d.from, d.from + l - 0.6)); }
  };
  const up = () => { drag.current = null; };

  const pct = (t: number) => `${dur ? (t / dur) * 100 : 0}%`;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Choose the part to use" onPointerDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="flex max-h-full w-full max-w-[760px] flex-col gap-3 overflow-hidden rounded-2xl bg-[#1d1726] p-4 text-white shadow-2xl" onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <div className="flex items-center gap-2">
          <Scissors className="h-4 w-4 text-[#a9f600]" />
          <span className="flex-1 truncate text-[14px] font-semibold">Choose the part to use{title ? <span className="font-normal text-[#b9b2c6]"> · {title}</span> : null}</span>
          <button type="button" onClick={onCancel} aria-label="Cancel" className="rounded-md p-1 text-[#b9b2c6] hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>

        <div className="relative grid min-h-0 place-items-center overflow-hidden rounded-xl bg-black" style={{ height: 'min(46vh, 420px)' }}>
          <video ref={video} src={url} muted={muted} playsInline preload="auto" onLoadedMetadata={onMeta} onClick={togglePlay} className="h-full w-full object-contain" />
          {dur == null && <span className="absolute inset-0 grid place-items-center text-[13px] text-[#b9b2c6]"><Loader2 className="h-5 w-5 animate-spin" /></span>}
          <div className="absolute bottom-2 left-2 flex gap-1.5">
            <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} className="grid h-8 w-8 place-items-center rounded-full bg-[#a9f600] text-[#2e024c]">{playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}</button>
            <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Sound on' : 'Sound off'} className="grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white">{muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</button>
          </div>
          <span className="absolute bottom-3 right-3 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[11.5px] tabular-nums">{fmt(Math.max(0, cur - from))} / {fmt(len)}</span>
        </div>

        {/* The whole source, the chosen part bright. */}
        <div ref={bar} className="relative h-14 cursor-pointer select-none overflow-hidden rounded-lg bg-[#2a2235]"
          onPointerDown={(e) => { if (dur == null) return; const t = secAt(e.clientX); place(t - len / 2, len); }}>
          {dur != null && (
            <div className="pointer-events-none absolute inset-0 flex">
              {Array.from({ length: THUMBS }, (_, k) => (
                <video key={k} src={`${url}#t=${((k + 0.5) / THUMBS) * dur}`} preload="metadata" muted playsInline className="h-full min-w-0 flex-1 object-cover opacity-70" />
              ))}
            </div>
          )}
          {dur != null && <>
            <div className="pointer-events-none absolute inset-y-0 left-0 bg-black/60" style={{ width: pct(from) }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 bg-black/60" style={{ left: pct(from + len) }} />
            <div onPointerDown={(e) => down(e, 'body')} title="Drag to use another part"
              className="absolute inset-y-0 cursor-grab rounded-md border-2 border-[#a9f600] active:cursor-grabbing" style={{ left: pct(from), width: pct(len) }}>
              <span onPointerDown={(e) => down(e, 'in')} title="Drag: start earlier or later (I)" className="absolute inset-y-0 -left-[2px] w-3 cursor-ew-resize bg-[#a9f600] after:absolute after:left-[4.5px] after:top-1/2 after:h-4 after:w-[3px] after:-translate-y-1/2 after:rounded after:bg-[#2e024c]" />
              <span onPointerDown={(e) => down(e, 'out')} title="Drag: end earlier or later (O)" className="absolute inset-y-0 -right-[2px] w-3 cursor-ew-resize bg-[#a9f600] after:absolute after:right-[4.5px] after:top-1/2 after:h-4 after:w-[3px] after:-translate-y-1/2 after:rounded after:bg-[#2e024c]" />
            </div>
            <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white" style={{ left: pct(cur) }} />
          </>}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="font-mono tabular-nums text-[#e4dfee]">{fmt(from)} → {fmt(from + len)}</span>
          <span className="text-[#7d7489]">of {dur != null ? fmt(dur) : '…'}</span>
          <div className="ml-1 flex gap-1">
            {[2, 3, 5].filter((s) => dur == null || s <= dur).map((s) => (
              <button key={s} type="button" onClick={() => place(from, s)} className={`rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${Math.abs(len - s) < 0.05 ? 'border-[#a9f600] text-[#a9f600]' : 'border-[#4a4157] text-[#e4dfee] hover:border-[#a9f600]'}`}>{s} s</button>
            ))}
            {dur != null && dur <= maxSeconds && <button type="button" onClick={() => place(0, dur)} className="rounded-full border border-[#4a4157] px-2 py-0.5 text-[11.5px] font-semibold text-[#e4dfee] hover:border-[#a9f600]">Whole clip</button>}
          </div>
          <span className="hidden flex-1 text-right text-[11px] text-[#7d7489] md:inline">I / O set start and end · ← → nudge · Enter to use</span>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg px-3 py-2 text-[13px] font-semibold text-[#e4dfee] hover:bg-white/10">Cancel</button>
          <button type="button" disabled={dur == null} onClick={confirm} className="inline-flex items-center gap-1.5 rounded-lg bg-[#a9f600] px-3.5 py-2 text-[13px] font-bold text-[#2e024c] hover:brightness-95 disabled:opacity-40"><Scissors className="h-4 w-4" />Use these {len.toFixed(1)} s</button>
        </div>
      </div>
    </div>
  );
}
